import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import logo from '../../images/logo/logo-black.png';
import CartIcon from '../cart/CartIcon';
import './ModernHeader.scss';

const ModernHeader = ({ forceSolid = false }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const location = useLocation();
    const dropdownRef = useRef(null);

    // Navigation structure - All menus with wide dropdown style
    const navigation = [
        {
            label: 'Markets Served',
            path: '/serve',
            isMegaMenu: true,
            children: [
                {
                    category: 'Government',
                    items: [
                        { label: 'Federal Civilian', path: '/federal-civilian', description: 'Federal Agency Solutions' },
                        { label: 'Department of Defense', path: '/department', description: 'Military & Defense Technology' },
                        { label: 'SLED', path: '/sled', description: 'State, Local & Education' },
                    ]
                },
                {
                    category: 'Private Sector',
                    items: [
                        { label: 'Commercial', path: '/commercial', description: 'Enterprise Solutions' },
                        { label: 'Healthcare', path: '/health-care', description: 'Medical & Health IT' },
                    ]
                }
            ]
        },
        {
            label: 'Products',
            path: '/accessories',
            isMegaMenu: true,
            children: [
                {
                    category: 'Hardware',
                    items: [
                        { label: 'Computer Systems', path: '/accessories?category=Computer Systems', description: 'Workstations & Servers' },
                        { label: 'Displays', path: '/accessories?category=Displays', description: 'Monitors & Display Solutions' },
                        { label: 'Network Devices', path: '/accessories?category=Network Devices', description: 'Routers, Switches & More' },
                        { label: 'Accessories', path: '/accessories?category=Accessories', description: 'IT Accessories & Peripherals' },
                    ]
                },
                {
                    category: 'Solutions',
                    items: [
                        { label: 'Software & Subscriptions', path: '/accessories?category=Software %26 Subscriptions', description: 'Licensed Software Solutions' },
                        { label: 'Storage Devices', path: '/accessories?category=Storage Devices', description: 'Enterprise Storage Systems' },
                        { label: 'Power & Rack Equipment', path: '/accessories?category=Power %26 Rack Equipment', description: 'Power & Rack Infrastructure' },
                        { label: 'Input & Output Devices', path: '/accessories?category=Input %26 Output Devices', description: 'Keyboards, Mice & More' },
                    ]
                }
            ]
        },
        {
            label: 'Our Services',
            path: '/service',
        },
        {
            label: 'Contracts',
            path: '/contracts',
        },
        {
            label: 'Core Competencies',
            path: '/solutions',
            isMegaMenu: true,
            children: [
                {
                    category: 'Technology Solutions',
                    items: [
                        { label: 'AI Automation & Workflows', path: '/ai-automation', description: 'Automations, Copilots & Make/n8n' },
                        { label: 'IT Modernization', path: '/it-modernization', description: 'Infrastructure & Cloud Services' },
                        { label: 'Workforce Experience', path: '/workforce', description: 'Digital Workplace Solutions' },
                        { label: 'Digital Transformation', path: '/digital', description: 'Automation & Innovation' },
                        { label: 'Cybersecurity', path: '/cybersecurity', description: 'Security & Compliance' },
                    ]
                },
                {
                    category: 'Defense Solutions',
                    items: [
                        { label: 'Army', path: '/army', description: 'Ground Forces Technology' },
                        { label: 'Navy', path: '/navy', description: 'Maritime Operations' },
                        { label: 'Air Force', path: '/air-force', description: 'Air Superiority Systems' },
                        { label: 'Space Force', path: '/space-force', description: 'Space Operations' },
                        { label: 'Marine Corps', path: '/marine-corps', description: 'Expeditionary Technology' },
                    ]
                }
            ]
        },
        {
            label: 'Company',
            path: '/about',
            isMegaMenu: true,
            children: [
                {
                    category: 'About',
                    items: [
                        { label: 'About Us', path: '/about', description: 'Our Story & Mission' },
                        { label: 'Contact Us', path: '/contact', description: 'Get in Touch' },
                    ]
                },
                {
                    category: 'Connect',
                    items: [
                        { label: 'Partners', path: '/partners', description: 'Our Trusted Partners' },
                    ]
                }
            ]
        },
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMenuOpen(false);
        setActiveDropdown(null);
    }, [location]);

    const handleNavClick = () => {
        window.scrollTo(0, 0);
    };

    const toggleDropdown = (index) => {
        setActiveDropdown(activeDropdown === index ? null : index);
    };

    return (
        <header className={`jk-header ${(isScrolled || forceSolid) ? 'jk-header--scrolled' : ''}`}>
            <div className="jk-header__container">
                {/* Logo */}
                <Link to="/" className="jk-header__logo" onClick={handleNavClick}>
                    <img
                        src={isScrolled ? logo : logo}
                        alt="Jaak Tech"
                        className="jk-header__logo-img"
                    />
                </Link>

                {/* Desktop Navigation */}
                <nav className="jk-header__nav" ref={dropdownRef}>
                    <ul className="jk-header__nav-list">
                        {navigation.map((item, index) => (
                            <li
                                key={index}
                                className={`jk-header__nav-item ${item.children ? 'has-dropdown' : ''}`}
                                onMouseEnter={() => item.children && setActiveDropdown(index)}
                                onMouseLeave={() => item.children && setActiveDropdown(null)}
                            >
                                <Link
                                    to={item.path}
                                    className="jk-header__nav-link"
                                    onClick={handleNavClick}
                                >
                                    {item.label}
                                    {item.children && <ChevronDown size={16} className="jk-header__nav-chevron" />}
                                </Link>

                                {/* Mega Menu */}
                                {item.isMegaMenu && item.children && (
                                    <div className={`jk-header__mega-menu ${activeDropdown === index ? 'is-active' : ''}`}>
                                        <div className="jk-header__mega-menu-inner">
                                            {item.children.map((section, sectionIndex) => (
                                                <div key={sectionIndex} className="jk-header__mega-menu-section">
                                                    <h4 className="jk-header__mega-menu-title">{section.category}</h4>
                                                    <ul className="jk-header__mega-menu-list">
                                                        {section.items.map((subItem, subIndex) => (
                                                            <li key={subIndex}>
                                                                <Link
                                                                    to={subItem.path}
                                                                    className="jk-header__mega-menu-link"
                                                                    onClick={handleNavClick}
                                                                >
                                                                    <span className="jk-header__mega-menu-label">{subItem.label}</span>
                                                                    <span className="jk-header__mega-menu-desc">{subItem.description}</span>
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Regular Dropdown Menu */}
                                {!item.isMegaMenu && item.children && (
                                    <div className={`jk-header__dropdown ${activeDropdown === index ? 'is-active' : ''}`}>
                                        <ul className="jk-header__dropdown-list">
                                            {item.children.map((child, childIndex) => (
                                                <li key={childIndex} className="jk-header__dropdown-item">
                                                    <Link
                                                        to={child.path}
                                                        className="jk-header__dropdown-link"
                                                        onClick={handleNavClick}
                                                    >
                                                        {child.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* CTA Button */}
                <div className="jk-header__actions">
                    <CartIcon isScrolled={isScrolled} />

                    <Link to="/contact" className="jk-header__cta" onClick={handleNavClick}>
                        <Phone size={18} />
                        <span>Contact Us</span>
                    </Link>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="jk-header__mobile-toggle"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className={`jk-header__mobile-menu ${isMenuOpen ? 'is-active' : ''}`}>
                <div className="jk-header__mobile-menu-inner">
                    <ul className="jk-header__mobile-nav">
                        {navigation.map((item, index) => (
                            <li key={index} className="jk-header__mobile-nav-item">
                                {item.children ? (
                                    <>
                                        <button
                                            className="jk-header__mobile-nav-btn"
                                            onClick={() => toggleDropdown(index)}
                                        >
                                            {item.label}
                                            <ChevronDown
                                                size={18}
                                                className={`jk-header__mobile-chevron ${activeDropdown === index ? 'is-rotated' : ''}`}
                                            />
                                        </button>
                                        <ul className={`jk-header__mobile-dropdown ${activeDropdown === index ? 'is-active' : ''}`}>
                                            {item.isMegaMenu ? (
                                                // Mega menu mobile rendering
                                                item.children.map((section, sectionIndex) => (
                                                    <li key={sectionIndex} className="jk-header__mobile-section">
                                                        <span className="jk-header__mobile-section-title">{section.category}</span>
                                                        {section.items.map((subItem, subIndex) => (
                                                            <Link
                                                                key={subIndex}
                                                                to={subItem.path}
                                                                className="jk-header__mobile-dropdown-link"
                                                                onClick={handleNavClick}
                                                            >
                                                                {subItem.label}
                                                            </Link>
                                                        ))}
                                                    </li>
                                                ))
                                            ) : (
                                                // Regular dropdown mobile rendering
                                                item.children.map((child, childIndex) => (
                                                    <li key={childIndex}>
                                                        <Link
                                                            to={child.path}
                                                            className="jk-header__mobile-dropdown-link"
                                                            onClick={handleNavClick}
                                                        >
                                                            {child.label}
                                                        </Link>
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    </>
                                ) : (
                                    <Link
                                        to={item.path}
                                        className="jk-header__mobile-nav-link"
                                        onClick={handleNavClick}
                                    >
                                        {item.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="jk-header__mobile-cta">
                        <Link to="/contact" className="jk-btn jk-btn--primary w-full" onClick={handleNavClick}>
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
                <div
                    className="jk-header__overlay"
                    onClick={() => setIsMenuOpen(false)}
                />
            )}
        </header>
    );
};

export default ModernHeader;
